import { AlertBox, Button, Link } from '@vp/swan';
import React, { useCallback, useContext, useEffect } from 'react';
import { CONVERSATION_CONSTANTS } from '../../config/miaw/constants';
import { CAContext } from '../../contexts/CAContext';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { useMIAWAgentActions } from '../../hooks/miaw/useMIAWAgentActions';
import { clearInMemoryData } from '../../utils/miaw/chat-config';
import { clearChatbotToken, clearChatSessionType, clearRestId } from '../../utils/session-storage-utils';

interface ErrorMessageProps {
  message?: string;
  forcePageRefresh?: boolean;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ forcePageRefresh }) => {
  const [message, setMessage] = React.useState<string | undefined>();

  const { handleCloseEventSource } = useMIAWAgentActions();

  const { setChatState } = useContext(CAContext);

  const {
    state: { showErrorMessage },
    setState: setChatbotState,
  } = useContext(ChatbotContext);
  const {
    state: { isMIAWApiError, isMIAWStartChatError },
    setState: setMIAWContext,
  } = useContext(MiawChatContext);

  useEffect(() => {
    if (showErrorMessage) {
      setMessage(`We're having trouble connecting to Vista Assistant.`);
    } else if (isMIAWApiError || isMIAWStartChatError) {
      setMessage(`We're having trouble connecting to Chat`);
    } else {
      setMessage('Something went wrong');
    }
  }, [showErrorMessage, isMIAWApiError, isMIAWStartChatError]);

  const handleRefresh = useCallback(() => {
    if (forcePageRefresh) {
      window.location.reload();
    }
    /** For chatbot error handling */
    if (showErrorMessage) {
      setChatbotState((prevState) => ({
        ...prevState,
        conversationMessages: [],
        discussionId: '',
        sessionId: '',
        showErrorMessage: false,
      }));
      clearChatbotToken();
      clearChatSessionType();
      clearRestId();
    } else if (isMIAWApiError) {
      /** For MIAW API error handling */
      handleCloseEventSource();
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: [],
        conversationId: '',
        agentName: '',
        conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION,
      }));
      clearInMemoryData();
      setChatbotState((prevState) => ({
        ...prevState,
        conversationMessages: [],
        discussionId: '',
        sessionId: '',
      }));
      clearChatbotToken();
      clearRestId();
      clearChatSessionType();
    }
    /** In both cases, reset to initial stage */
    setChatState((prevState) => ({
      ...prevState,
      // chatWindow: 'minimized',
      chatSession: 'initial',
      isPrivacyPolicyAccepted: false,
    }));
  }, []);

  return (
    <AlertBox skin="error" m={5} style={{ width: 'initial' }}>
      {message}{' '}
      <Link
        render={(p) => (
          <Button skin="link" className={p.className} onClick={handleRefresh} autoFocus>
            {p.children}
          </Button>
        )}
      >
        Refresh to try again.
      </Link>
    </AlertBox>
  );
};

export default ErrorMessage;
